import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { DatePicker } from 'antd';

import Table from 'components/Table';
import { Typography, Collapse, Button, Tooltip } from 'ui-kit';

import { useOwnerDetails } from 'api/owners';
import { useOwnerPayoutNote, useOwnerPayouts } from 'api/owner_sdk';
import { useSignedUser } from 'api/auth';

import { formatCurrency } from 'utils';
import { getFirstAndLastDayOfCurrentMonth, MONTHS_MAP } from 'utils/datetime';

import OrderProductDetailsModal from './OrderProductDetailsModal';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import './PayoutList.scss';
import PayoutsExportButton from './PayoutsExportButton';
import PayoutSplitExportButton from './PayoutSplitExportButton';

const { RangePicker } = DatePicker;

const DEFAULT_RECIPIENT_NAME = 'Store Owner';

const normalizeRecipientName = (name: string): string => {
  if (!name) return DEFAULT_RECIPIENT_NAME;
  const trimmed = name.trim();
  const lower = trimmed.toLowerCase();

  if (
    lower === 'storeowner' ||
    lower === 'store_owner' ||
    lower === 'store owner' ||
    lower === 'default store owner'
  ) {
    return DEFAULT_RECIPIENT_NAME;
  }

  return trimmed
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getStoreSplits = (store: any): Record<string, number> => {
  if (!store) return {};

  const isSplitActive =
    store.is_custom_split_active ?? store.isCustomSplitActive ?? store.isSplitActive ?? false;

  if (!isSplitActive) {
    return { [DEFAULT_RECIPIENT_NAME]: 100 };
  }

  const splits =
    store.custom_store_splits ??
    store.customStoreSplits ??
    store.payoutSplit ??
    store.payout_split ??
    {};

  if (typeof splits === 'string') {
    try {
      return JSON.parse(splits);
    } catch (e) {
      return { [DEFAULT_RECIPIENT_NAME]: 100 };
    }
  }

  return splits;
};

const PayoutStatus = {
  PAYING: 'Paying',
  COMP: 'Completed',
  FAIL: 'Failed',
  CNCL: 'Cancelled',
  STRIPE: 'Stripe',
};

const NOW = new Date();

const PayoutsList = () => {
  const { firstDayOfMonth, lastDayOfMonth } = getFirstAndLastDayOfCurrentMonth(NOW);

  const [startDate, setStartDate] = useState<Date>(firstDayOfMonth);
  const [endDate, setEndDate] = useState<Date>(lastDayOfMonth);
  const [params, setParams] = useState({ startDate, endDate });

  // Show Order Product details V2
  const [currentRecord, setCurrentRecord] = useState(null);
  const [visibleModal, setVisibleModal] = useState(false);

  const { data: ownerPayoutDetails, isLoading } = useOwnerPayouts(params);
  const { data: ownerPayoutNote } = useOwnerPayoutNote();
  const { data: user } = useSignedUser();
  const { data: owner } = useOwnerDetails(user?.owner_id);

  useEffect(() => {
    setParams({ startDate, endDate });
  }, [startDate, endDate]);

  const onChangeRange = (arr) => {
    const start = new Date(arr[0]);
    const end = new Date(arr[1]);
    setStartDate(start);
    const { lastDayOfMonth } = getFirstAndLastDayOfCurrentMonth(end);
    setEndDate(lastDayOfMonth);
  };

  const totalPayouts = useMemo(
    () => _.sumBy(ownerPayoutDetails, 'total_owner_amount_after_balances'),
    [ownerPayoutDetails],
  );

  const shouldShowPayoutNote = !!ownerPayoutNote?.note && totalPayouts < 15;

  const dynamicRecipientColumns = useMemo(() => {
    if (!ownerPayoutDetails || !Array.isArray(ownerPayoutDetails)) return [];

    const recipientsSet = new Set<string>([DEFAULT_RECIPIENT_NAME]);

    ownerPayoutDetails.forEach((record: any) => {
      const splitData = getStoreSplits(record?.store);

      Object.keys(splitData).forEach((rawName) => {
        const clean = normalizeRecipientName(rawName);
        if (clean) recipientsSet.add(clean);
      });
    });

    const recipientList = Array.from(recipientsSet).sort();

    return recipientList.map((recipientName) => ({
      title: recipientName,
      key: `recipient_${recipientName}`,
      render: (_text: any, record: any) => {
        const totalAmount = Number(record.total_owner_amount_after_balances) || 0;
        const splitData = getStoreSplits(record?.store);
        const entries = Object.entries(splitData);

        let recipientPercent = 0;
        let hasRecipient = false;

        if (entries.length > 0) {
          entries.forEach(([rawName, percent]) => {
            if (normalizeRecipientName(rawName) === recipientName) {
              recipientPercent =
                typeof percent === 'number' ? percent : parseFloat(percent as any) || 0;
              hasRecipient = true;
            }
          });
        } else if (recipientName === DEFAULT_RECIPIENT_NAME) {
          recipientPercent = 100;
          hasRecipient = true;
        }

        if (!hasRecipient || recipientPercent <= 0) {
          return '-';
        }

        const calculatedAmount = (totalAmount * recipientPercent) / 100;

        return (
          <Typography variant="body2">
            {formatCurrency(calculatedAmount, record.currency)}{' '}
            <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
              ({recipientPercent.toFixed(2)}%)
            </span>
          </Typography>
        );
      },
    }));
  }, [ownerPayoutDetails]);

  const baseColumns = [
    {
      title: 'Month',
      dataIndex: 'last_date_created',
      key: 'last_date_created',
      render: (text) => <>{MONTHS_MAP[new Date(text).getUTCMonth() + 1]}</>,
    },
    {
      title: 'Year',
      dataIndex: 'last_date_created',
      key: 'last_date_created',
      render: (text) => <>{new Date(text).getUTCFullYear()}</>,
    },
    {
      title: 'Store',
      dataIndex: 'store',
      key: 'store',
      render: (_text, record) => <>{record.store.store_name}</>,
    },
    {
      title: 'Payout Amount',
      dataIndex: 'total_owner_amount_after_balances',
      key: 'total_owner_amount_after_balances',
      render: (_text, record) => (
        <>{formatCurrency(record.total_owner_amount_after_balances, record.currency)}</>
      ),
    },
    ...dynamicRecipientColumns,
    {
      title: (
        <>
          <span>Payout Status</span>{' '}
          <Tooltip title="Payouts are processed on the 7th of the month and take on average 1-4 business days to arrive. Payouts occur the following month of the service date.">
            <ExclamationCircleOutlined />
          </Tooltip>
        </>
      ),
      dataIndex: 'payouts',
      key: 'payouts',
      render: (_text, record) => (
        <>
          {record.order_products_are_paid
            ? record.payouts.map((p) => PayoutStatus[p.status]).join(', ')
            : 'Upcoming'}
        </>
      ),
    },
    {
      render: (_text, record) => (
        <>
          <Button
            onClick={() => {
              setCurrentRecord(record);
              setVisibleModal(true);
            }}
          >
            View details
          </Button>

          {visibleModal && (
            <OrderProductDetailsModal
              owner={owner}
              storeId={currentRecord?.store.id}
              payoutStartDate={new Date(currentRecord?.first_date_created)}
              payoutEndDate={new Date(currentRecord?.last_date_created)}
              handleClose={() => {
                setVisibleModal(false);
                setCurrentRecord(null);
              }}
            />
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <Collapse
        header={() => <Typography variant="h6">Payouts</Typography>}
        initialCollpase={false}
      >
        <RangePicker
          value={[moment(startDate), moment(endDate)]}
          onChange={onChangeRange}
          allowClear={false}
          picker="month"
          format={'MMMM-YYYY'}
          className="PayoutCalendar"
        />

        <div className="DashboardPayouts_body">
          <Table
            loading={isLoading}
            scroll={{ x: '100%' }}
            className="DashboardPayouts_table"
            dataSource={ownerPayoutDetails}
            columns={baseColumns}
          />

          <div className="DashboardPayouts_totalsAndExport">
            <div>Total payout amount: {formatCurrency(totalPayouts, 'USD')}</div>
            <div className="DashboardPayouts_exportButtonsWrapper">
              <PayoutsExportButton startDate={startDate} endDate={endDate} />
              <PayoutSplitExportButton startDate={startDate} endDate={endDate} />
            </div>
          </div>
        </div>

        <Typography variant="caption">
          * Total may not be exact due to process timing, partial refunds, and cancellations.
        </Typography>
      </Collapse>

      {shouldShowPayoutNote && (
        <div className="DashboardPayouts_notesWrapper">
          <div className="DashboardPayouts_notesBox">
            <Typography variant="h6">Notes</Typography>
            <div className="DashboardPayouts_notesText">{ownerPayoutNote.note}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default PayoutsList;
